import dinero from 'dinero.js';
import { useEffect, useRef, useState } from 'react';

export const formatUsdMoney = (value: number) =>
    dinero({
        amount: Math.round(value * 100),
        currency: 'USD',
    }).toFormat('$0,0.00');

/** Eases displayed totals toward `target` when summary data updates (e.g. stats loading in). */
export const useAnimatedSummaryNumber = (target: number): number => {
    const displayRef = useRef(0);
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        const safeTarget = Number(target);
        if (!Number.isFinite(safeTarget)) {
            return;
        }

        const start = displayRef.current;
        const delta = Math.abs(safeTarget - start);
        if (delta < 1e-6) {
            return;
        }

        const durationMs = Math.min(900, Math.max(200, 220 + Math.min(delta, 5_000_000) / 25_000));

        let rafId = 0;
        const t0 = performance.now();

        const tick = (now: number) => {
            const elapsed = now - t0;
            const t = Math.min(1, elapsed / durationMs);
            const eased = 1 - (1 - t) * (1 - t);
            const next = start + (safeTarget - start) * eased;
            displayRef.current = next;
            setDisplay(next);
            if (t < 1) {
                rafId = requestAnimationFrame(tick);
            } else {
                displayRef.current = safeTarget;
                setDisplay(safeTarget);
            }
        };

        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [target]);

    return display;
};
